// Attendance statistics for the Report Builder (Phase 2). One shared
// computation powering BOTH the attendance-derived header fields (via the
// resolver registry) and the attendance report section — so a report's
// header numbers and its charts can never disagree.
//
// Design: a PURE core (`projectOccurrences` + `computeAttendanceStats`) plus a
// thin `fetchAttendanceStats` that takes an axios instance. The app preview
// passes the shared `api` instance (Amplify session); the Puppeteer PDF page
// passes a raw axios instance carrying the forwarded JWT — the same split
// PdfReportRoute already uses for posts/child/template. Row→shape mapping
// lives here (not reused from the data services, whose mappers are private)
// so the report feature is self-contained.
//
// Parity note: mirrors the Attendance dashboard's semantics (AttendanceCharts)
// — a 5-minute-ish grace via orgConfig, "attended" = a logged arrival time,
// daily rules skipped (they don't fit the weekly model) — so the report's
// attendance rate matches the number staff see on the dashboard.

import { AxiosInstance } from 'axios';
import { toDateString, getMonday } from './dateUtils';
import { occursOnDate, ruleFromRow } from '../components/schedule/scheduleOccurrences';

// ── Raw row shapes (subset actually needed) ─────────────────────────────────

interface RawScheduleItem {
  id: number;
  day_of_week: number;                 // 0 = Monday … 6 = Sunday
  start_time: string;                  // 'HH:MM:SS'
  end_time: string;
  child_id: number | null;
  repeat_interval: 'daily' | 'weekly' | 'bi-weekly' | 'monthly';
  start_date: string;                  // ISO / 'YYYY-MM-DD…'
  end_date: string | null;
}

interface RawScheduleException {
  schedule_item_id: number;
  exception_date: string;
  status: 'cancelled' | 'modified';
}

interface RawAttendance {
  schedule_item_id: number;
  child_id: number;
  attendance_date: string;
  actual_arrival: string | null;
  actual_departure: string | null;
  arrival_comment: string | null;
}

// ── Normalised shapes ───────────────────────────────────────────────────────

interface NormSession {
  serverId: number;
  dayIdx: number;                      // 0 = Monday … 6 = Sunday
  startMins: number;
  endMins: number;
  childId: number;
  interval: RawScheduleItem['repeat_interval'];
  startDate: string;                   // YYYY-MM-DD
  endDate: string | null;
  isOnce: boolean;                     // weekly rule pinned to a single date
}

interface AttendanceLog {
  scheduleItemId: number;
  childId: number;
  date: string;                        // YYYY-MM-DD
  arrival: string | null;              // 'HH:MM'
  departure: string | null;
  arrivalComment: string | null;
}

export interface SessionOccurrence {
  date: string;                        // YYYY-MM-DD
  weekStart: string;                   // YYYY-MM-DD (Monday)
  dayIdx: number;                      // 0 = Monday … 6 = Sunday
  startMins: number;
  endMins: number;
  durationMins: number;
  attendance: AttendanceLog | null;
}

export interface NoShowReasonSlice { reason: string; count: number; }

/** One stacked bar in the punctuality chart (arrivals, then departures). */
export interface PunctualitySlice {
  name: 'Arrival' | 'Departure';
  early: number;
  onTime: number;
  late: number;
}

/**
 * One scheduled occurrence as a timeline row. Times are minutes from midnight
 * so the chart can plot them on a numeric axis; `label` is pre-disambiguated
 * (a date with two sessions gets its start time appended) because recharts
 * merges categories that share a value.
 */
export interface TimelineRow {
  label: string;
  date: string;                        // YYYY-MM-DD
  schedStart: number;
  schedEnd: number;
  actualStart: number | null;
  actualEnd: number | null;
  status: 'attended' | 'absent' | 'unlogged';
}

export interface AttendanceStats {
  /** Any scheduled occurrence fell in the period. Header resolvers omit their
   *  row and the section renders nothing when this is false. */
  hasSchedule: boolean;
  sessionsScheduled: number;
  sessionsAttended: number;            // occurrences with a logged arrival
  sessionsNoShow: number;              // logged, but no arrival = absence
  sessionsUnlogged: number;            // past occurrence with no record
  /** % attended of scheduled; null when nothing was scheduled. */
  attendanceRate: number | null;
  hoursScheduled: number;              // decimal hours (1dp)
  hoursAttended: number;
  daysAttended: number;                // distinct dates with a logged arrival
  noShowReasons: NoShowReasonSlice[];  // absence reason breakdown
  arrivalsOnTime: number;
  arrivalsTotal: number;               // occurrences with a logged arrival time
  arrivalsEarly: number;               // arrived more than grace BEFORE start
  arrivalsLate: number;                // arrived more than grace AFTER start
  departuresTotal: number;             // occurrences with a logged departure
  departuresOnTime: number;
  departuresEarly: number;             // left more than grace BEFORE the end
  departuresLate: number;
  /** Mean minutes after the scheduled start across late arrivals only; 0 when
   *  there are none. Reported alongside the count so one very late day can't
   *  be mistaken for a pattern. */
  avgMinutesLate: number;
  /** The grace window the punctuality figures were classified against, so the
   *  section can label the chart honestly rather than assume a constant. */
  graceMinutes: number;
  /** Distinct scheduled weekday names, Monday-first. */
  sessionDays: string[];
  // Chart-ready series (fixed-dimension recharts in the report/PDF path).
  weeklyTrend: { week: string; rate: number; scheduled: number; attended: number }[];
  hoursPerWeek: { week: string; scheduled: number; attended: number }[];
  /** Hours by weekday across the whole period — the per-day analogue of
   *  hoursPerWeek, which is what a single-week report can actually plot. */
  hoursPerDay: { day: string; scheduled: number; attended: number }[];
  outcome: { name: string; value: number; fill: string }[];
  byDayOfWeek: { day: string; scheduled: number; attended: number; rate: number }[];
  punctuality: PunctualitySlice[];
  /** Chronological, one row per scheduled occurrence. */
  sessionTimeline: TimelineRow[];
}

// ── Palette (matches AttendanceCharts) ──────────────────────────────────────

const COLOURS = {
  attended: '#26923f',
  noShow: '#b8302f',
  unlogged: '#aaaaaa',
  scheduledBar: '#9ec5a6',
  attendedBar: '#26923f',
};

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ── Pure helpers ────────────────────────────────────────────────────────────

const dateOnly = (v: unknown): string | null =>
  typeof v === 'string' && v.length >= 10 ? v.substring(0, 10) : null;

/** First HH:MM in a timestamp-like string (the date's digits never match). */
const toHHMM = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const m = v.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : null;
};

const timeToMins = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

const shortDate = (dateStr: string): string =>
  new Date(dateStr + 'T00:00').toLocaleDateString(undefined, { day: '2-digit', month: 'short' });

/** "Mon 06 Jul" — the timeline's row label. */
const timelineDate = (dateStr: string): string =>
  new Date(dateStr + 'T00:00').toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });

const minsToHHMM = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

type Timing = 'early' | 'on-time' | 'late';

/** Actual vs scheduled against a symmetric grace window (mirrors the dashboard). */
const classifyTiming = (scheduledMins: number, actualMins: number, grace: number): Timing => {
  const delta = actualMins - scheduledMins;
  if (Math.abs(delta) <= grace) return 'on-time';
  return delta < 0 ? 'early' : 'late';
};

/** dayIdx 0=Mon..6=Sun for a local date. */
const dayIdxOf = (d: Date): number => {
  const raw = d.getDay();
  return raw === 0 ? 6 : raw - 1;
};

// The same occurrence rule every schedule screen uses — see scheduleOccurrences.
// A 'daily' rule has no OccurrenceRule and never occurs, mirroring the
// Attendance dashboard so report numbers match what staff see.
function sessionActiveOnDate(s: NormSession, dateStr: string): boolean {
  const rule = ruleFromRow(s);
  return rule !== null && occursOnDate(rule, new Date(dateStr + 'T00:00'));
}

/** "Reason — free text" comment → a short bucket label (mirrors the dashboard). */
function bucketReason(comment: string | null): string {
  if (!comment) return 'Unspecified';
  const head = comment.split('—')[0].trim().toLowerCase();
  if (!head) return 'Unspecified';
  if (head.includes('ill') || head.includes('sick')) return 'Illness';
  if (head.includes('holiday') || head.includes('vac')) return 'Holiday';
  if (head.includes('family')) return 'Family';
  if (head.includes('appoint')) return 'Appointment';
  if (head.includes('transport')) return 'Transport';
  if (head.includes('unauth')) return 'Unauthorised';
  return 'Other';
}

// ── Projection ──────────────────────────────────────────────────────────────

/**
 * Expand recurring rules into concrete session occurrences across [from, to]
 * for one child, dropping cancelled dates and matching any logged attendance.
 */
export function projectOccurrences(
  sessions: NormSession[],
  cancelledByItem: Map<number, Set<string>>,
  attendance: AttendanceLog[],
  childId: number,
  from: string,
  to: string,
): SessionOccurrence[] {
  const out: SessionOccurrence[] = [];
  const startD = new Date(from + 'T00:00');
  const endD = new Date(to + 'T00:00');
  const mine = sessions.filter(s => s.childId === childId);
  if (mine.length === 0) return out;

  for (let d = new Date(startD); d.getTime() <= endD.getTime(); d.setDate(d.getDate() + 1)) {
    const dateStr = toDateString(d);
    const weekStart = toDateString(getMonday(d));
    const dayIdx = dayIdxOf(d);
    for (const s of mine) {
      if (!sessionActiveOnDate(s, dateStr)) continue;
      if (cancelledByItem.get(s.serverId)?.has(dateStr)) continue;
      const att = attendance.find(
        a => a.scheduleItemId === s.serverId && a.childId === childId && a.date === dateStr,
      ) ?? null;
      out.push({
        date: dateStr,
        weekStart,
        dayIdx,
        startMins: s.startMins,
        endMins: s.endMins,
        durationMins: Math.max(0, s.endMins - s.startMins),
        attendance: att,
      });
    }
  }
  return out;
}

// ── Stat derivation ─────────────────────────────────────────────────────────

export function computeAttendanceStats(
  occurrences: SessionOccurrence[],
  graceMinutes: number,
): AttendanceStats {
  const today = toDateString(new Date());

  let attended = 0, noShow = 0, unlogged = 0;
  let schedMins = 0, actualMins = 0;
  let arrivalsOnTime = 0, arrivalsTotal = 0, arrivalsEarly = 0, arrivalsLate = 0;
  let departuresTotal = 0, departuresOnTime = 0, departuresEarly = 0, departuresLate = 0;
  let lateMinsTotal = 0;
  const attendedDates = new Set<string>();
  const reasonCounts = new Map<string, number>();
  const dayIdxSeen = new Set<number>();

  const byWeek = new Map<string, { scheduled: number; attended: number; schedMins: number; actualMins: number }>();
  const byDay = WEEKDAY_SHORT.map(day => ({ day, scheduled: 0, attended: 0, rate: 0 }));
  const hoursByDay = WEEKDAY_SHORT.map(day => ({ day, schedMins: 0, actualMins: 0 }));
  // Two sessions on one date would collide as a chart category — count first,
  // then disambiguate the labels below.
  const perDateCount = new Map<string, number>();
  const timeline: TimelineRow[] = [];

  for (const o of occurrences) {
    dayIdxSeen.add(o.dayIdx);
    schedMins += o.durationMins;
    byDay[o.dayIdx].scheduled += 1;
    hoursByDay[o.dayIdx].schedMins += o.durationMins;
    perDateCount.set(o.date, (perDateCount.get(o.date) ?? 0) + 1);
    const wk = byWeek.get(o.weekStart) ?? { scheduled: 0, attended: 0, schedMins: 0, actualMins: 0 };
    wk.scheduled += 1;
    wk.schedMins += o.durationMins;

    const att = o.attendance;
    let rowStatus: TimelineRow['status'] = 'unlogged';
    let actualStart: number | null = null;
    let actualEnd: number | null = null;

    if (att?.arrival) {
      attended += 1;
      rowStatus = 'attended';
      attendedDates.add(o.date);
      byDay[o.dayIdx].attended += 1;
      wk.attended += 1;

      const arrivalMins = timeToMins(att.arrival);
      actualStart = arrivalMins;
      arrivalsTotal += 1;
      const arrivalTiming = classifyTiming(o.startMins, arrivalMins, graceMinutes);
      if (arrivalTiming === 'on-time') arrivalsOnTime += 1;
      else if (arrivalTiming === 'early') arrivalsEarly += 1;
      else { arrivalsLate += 1; lateMinsTotal += arrivalMins - o.startMins; }

      if (att.departure) {
        const departureMins = timeToMins(att.departure);
        actualEnd = departureMins;
        departuresTotal += 1;
        const departureTiming = classifyTiming(o.endMins, departureMins, graceMinutes);
        if (departureTiming === 'on-time') departuresOnTime += 1;
        else if (departureTiming === 'early') departuresEarly += 1;
        else departuresLate += 1;

        const mins = Math.max(0, departureMins - arrivalMins);
        actualMins += mins;
        wk.actualMins += mins;
        hoursByDay[o.dayIdx].actualMins += mins;
      } else {
        // Arrived, no sign-out: count to scheduled end as a conservative proxy.
        // The timeline draws the same proxy so the bar and the hours agree.
        actualEnd = Math.max(arrivalMins, o.endMins);
        const mins = Math.max(0, o.endMins - arrivalMins);
        actualMins += mins;
        wk.actualMins += mins;
        hoursByDay[o.dayIdx].actualMins += mins;
      }
    } else if (att) {
      noShow += 1;
      rowStatus = 'absent';
      const bucket = bucketReason(att.arrivalComment);
      reasonCounts.set(bucket, (reasonCounts.get(bucket) ?? 0) + 1);
    } else if (o.date < today) {
      unlogged += 1;
    }

    timeline.push({
      label: timelineDate(o.date),
      date: o.date,
      schedStart: o.startMins,
      schedEnd: o.endMins,
      actualStart,
      actualEnd,
      status: rowStatus,
    });
    byWeek.set(o.weekStart, wk);
  }

  for (const row of timeline) {
    if ((perDateCount.get(row.date) ?? 0) > 1) row.label = `${row.label} · ${minsToHHMM(row.schedStart)}`;
  }
  timeline.sort((a, b) => a.date.localeCompare(b.date) || a.schedStart - b.schedStart);

  for (const b of byDay) b.rate = b.scheduled === 0 ? 0 : Math.round((b.attended / b.scheduled) * 100);

  const scheduled = occurrences.length;
  const toHours = (mins: number) => +(mins / 60).toFixed(1);

  const weeklyTrend = Array.from(byWeek.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, v]) => ({
      week: shortDate(weekStart),
      rate: v.scheduled === 0 ? 0 : Math.round((v.attended / v.scheduled) * 100),
      scheduled: v.scheduled,
      attended: v.attended,
    }));

  const hoursPerWeek = Array.from(byWeek.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, v]) => ({
      week: shortDate(weekStart),
      scheduled: toHours(v.schedMins),
      attended: toHours(v.actualMins),
    }));

  // Weekdays with nothing scheduled all period are dropped — a report for a
  // Mon/Wed/Fri placement shouldn't print three empty columns.
  const hoursPerDay = hoursByDay
    .filter(d => d.schedMins > 0)
    .map(d => ({ day: d.day, scheduled: toHours(d.schedMins), attended: toHours(d.actualMins) }));

  const punctuality: PunctualitySlice[] = [
    { name: 'Arrival', early: arrivalsEarly, onTime: arrivalsOnTime, late: arrivalsLate },
    { name: 'Departure', early: departuresEarly, onTime: departuresOnTime, late: departuresLate },
  ];

  const outcome = [
    { name: 'Attended', value: attended, fill: COLOURS.attended },
    { name: 'No-show', value: noShow, fill: COLOURS.noShow },
    { name: 'Not logged', value: unlogged, fill: COLOURS.unlogged },
  ].filter(d => d.value > 0);

  const noShowReasons = Array.from(reasonCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => ({ reason, count }));

  const sessionDays = Array.from(dayIdxSeen).sort((a, b) => a - b).map(i => WEEKDAY_FULL[i]);

  return {
    hasSchedule: scheduled > 0,
    sessionsScheduled: scheduled,
    sessionsAttended: attended,
    sessionsNoShow: noShow,
    sessionsUnlogged: unlogged,
    attendanceRate: scheduled === 0 ? null : Math.round((attended / scheduled) * 100),
    hoursScheduled: toHours(schedMins),
    hoursAttended: toHours(actualMins),
    daysAttended: attendedDates.size,
    noShowReasons,
    arrivalsOnTime,
    arrivalsTotal,
    arrivalsEarly,
    arrivalsLate,
    departuresTotal,
    departuresOnTime,
    departuresEarly,
    departuresLate,
    avgMinutesLate: arrivalsLate === 0 ? 0 : Math.round(lateMinsTotal / arrivalsLate),
    graceMinutes,
    sessionDays,
    weeklyTrend,
    hoursPerWeek,
    hoursPerDay,
    outcome,
    byDayOfWeek: byDay,
    punctuality,
    sessionTimeline: timeline,
  };
}

// ── Fetch + assemble ────────────────────────────────────────────────────────

function normSession(row: RawScheduleItem): NormSession | null {
  const startDate = dateOnly(row.start_date);
  if (startDate === null) return null;
  const endDate = dateOnly(row.end_date);
  // 'once' is stored as a weekly rule pinned to one date (end === start), since
  // the DB CHECK disallows a 'once' interval (see scheduleDataService).
  const isOnce = row.repeat_interval === 'weekly' && endDate === startDate;
  return {
    serverId: row.id,
    dayIdx: row.day_of_week,
    startMins: timeToMins((row.start_time || '').substring(0, 5)),
    endMins: timeToMins((row.end_time || '').substring(0, 5)),
    childId: row.child_id ?? -1,
    interval: row.repeat_interval,
    startDate,
    endDate,
    isOnce,
  };
}

function normAttendance(row: RawAttendance): AttendanceLog | null {
  const date = dateOnly(row.attendance_date);
  if (date === null) return null;
  return {
    scheduleItemId: row.schedule_item_id,
    childId: row.child_id,
    date,
    arrival: row.actual_arrival ? toHHMM(row.actual_arrival) : null,
    departure: row.actual_departure ? toHHMM(row.actual_departure) : null,
    arrivalComment: row.arrival_comment ?? null,
  };
}

/**
 * Load + compute attendance stats for one child over a period using the given
 * axios instance (app `api` for preview, a JWT-bearing instance for the PDF
 * page). Returns null on any fetch failure — attendance is supplementary, so a
 * report should still render without it (the section/rows just omit).
 */
export async function fetchAttendanceStats(
  http: AxiosInstance,
  childId: number,
  from: string,
  to: string,
  graceMinutes: number,
): Promise<AttendanceStats | null> {
  try {
    const [itemsRes, exceptionsRes, attendanceRes] = await Promise.all([
      http.get('/schedule/items'),
      http.get('/schedule/exceptions'),
      http.get('/attendance', { params: { from, to, childId } }),
    ]);

    const sessions = (Array.isArray(itemsRes.data) ? itemsRes.data : [])
      .map(normSession)
      .filter((s: NormSession | null): s is NormSession => s !== null);

    const cancelledByItem = new Map<number, Set<string>>();
    for (const ex of (Array.isArray(exceptionsRes.data) ? exceptionsRes.data : []) as RawScheduleException[]) {
      if (ex.status !== 'cancelled') continue;
      const date = dateOnly(ex.exception_date);
      if (!date) continue;
      const set = cancelledByItem.get(ex.schedule_item_id) ?? new Set<string>();
      set.add(date);
      cancelledByItem.set(ex.schedule_item_id, set);
    }

    const attendance = (Array.isArray(attendanceRes.data) ? attendanceRes.data : [])
      .map(normAttendance)
      .filter((a: AttendanceLog | null): a is AttendanceLog => a !== null);

    const occurrences = projectOccurrences(sessions, cancelledByItem, attendance, childId, from, to);
    return computeAttendanceStats(occurrences, graceMinutes);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[attendanceStatsService] failed to load attendance stats', err);
    return null;
  }
}
